import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Send,
  Users,
  CheckCircle,
  XCircle,
  Activity,
  Calendar,
  MailOpen,
  Mail,
  ArrowLeft,
  MousePointerClick,
  Search,
  Download,
  MoreVertical,
  PlayCircle,
  ExternalLink,
  Link2,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";
import { Link } from "react-router-dom";

const AllMessageHistory = () => {
  const [user] = useAuthState(auth);
  const [serverData, setServerData] = useState({
    logs: [],
    totalPages: 1,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [linkClickData, setLinkClickData] = useState(null);
  const [linkClickLoading, setLinkClickLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'not_opened', 'clicked'

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Re-fetch data when inputs change
  useEffect(() => {
    const fetchPaginatedData = async () => {
      if (!user?.email) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `https://emailserverjuly.shahlimon.online/api/analytics/paginated-logs`,
          {
            params: {
              ownerEmail: user.email,
              page: currentPage,
              limit: itemsPerPage,
              search: searchQuery,
              tab: activeTab,
            },
          },
        );

        // Add timeline icons back (functions can't be sent over JSON)
        const mappedLogs = res.data.logs.map((log) => {
          if (log.tracking && log.tracking.timeline) {
            log.tracking.timeline = log.tracking.timeline.map((ev) => ({
              ...ev,
              icon:
                ev.type === "sent"
                  ? Send
                  : ev.type === "opened"
                    ? MailOpen
                    : MousePointerClick,
            }));
          }
          return log;
        });

        setServerData({
          logs: mappedLogs,
          totalPages: res.data.totalPages || 1,
          totalRecords: res.data.totalRecords || 0,
        });
      } catch (error) {
        console.error("Error fetching paginated data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaginatedData();
  }, [user, currentPage, searchQuery, activeTab]);

  // Fetch per-link click breakdown whenever a log detail is opened
  useEffect(() => {
    const fetchLinkClicks = async () => {
      const eid = selectedLogDetail?.trackingId;
      if (!eid) {
        setLinkClickData(null);
        return;
      }
      setLinkClickLoading(true);
      try {
        const res = await axios.get(
          `https://emailserverjuly.shahlimon.online/api/analytics/link-clicks/${eid}`
        );
        setLinkClickData(res.data);
      } catch (err) {
        console.error("Error fetching link-click data:", err);
        setLinkClickData(null);
      } finally {
        setLinkClickLoading(false);
      }
    };
    fetchLinkClicks();
  }, [selectedLogDetail]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeTab]);

  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    const fetchContacts = async () => {
      if (!user?.email) return;
      try {
        const response = await axios.get(
          `https://emailserverjuly.shahlimon.online/api/user-email-templates/${user.email}`
        );
        setContacts(response.data);
      } catch (err) {
        console.error("Error fetching contacts:", err);
      }
    };
    fetchContacts();
  }, [user]);

  const contactNameMap = useMemo(() => {
    const map = {};
    contacts.forEach((c) => {
      if (c.UserEmail) {
        map[c.UserEmail.toLowerCase().trim()] = c.userName;
      }
    });
    return map;
  }, [contacts]);

  const getCurrentName = (log) => {
    if (!log) return "Unknown Contact";
    if (log.recipientEmail) {
      return contactNameMap[log.recipientEmail.toLowerCase().trim()] || log.recipientName || "Unknown Contact";
    }
    return log.recipientName || "Unknown Contact";
  };

  const totalPages = serverData.totalPages;
  const paginatedActivity = serverData.logs;

  // Helper to generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const exportToCSV = async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(
        `https://emailserverjuly.shahlimon.online/api/analytics/paginated-logs`,
        {
          params: {
            ownerEmail: user.email,
            search: searchQuery,
            tab: activeTab,
            export: true,
          },
        },
      );

      const logsToExport = res.data.logs || [];
      if (logsToExport.length === 0) return;

      const headers = [
        "Recipient Name",
        "Recipient Email",
        "Subject",
        "Date",
        "Status",
        "Opens",
        "Clicks",
      ];
      const csvContent = [
        headers.join(","),
        ...logsToExport.map((log) => {
          const t = log.tracking || {};
          const status = t.isClicked
            ? "Clicked"
            : t.isOpened
              ? "Opened"
              : t.isDelivered
                ? "Delivered"
                : "Sent";
          return [
            `"${log.recipientName || ""}"`,
            `"${log.recipientEmail || ""}"`,
            `"${log.subject || log.templateName || ""}"`,
            `"${log.date ? new Date(log.date).toLocaleString() : ""}"`,
            `"${status}"`,
            `"${t.openCount || 0}"`,
            `"${t.clickCount || 0}"`,
          ].join(",");
        }),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `message_history_export_${format(new Date(), "yyyy-MM-dd")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
    }
  };

  if (loading && serverData.logs.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-dark-main">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        />
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-dark-main py-4 px-3 px-lg-4">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Link
              to="/admin/email-marketing"
              className="btn btn-sm btn-link text-muted p-0 text-decoration-none hover-text-white transition-all"
            >
              <ArrowLeft size={16} className="me-1" /> Dashboard
            </Link>
          </div>
          <h1 className="fw-bold text-white fs-3 mb-0 d-flex align-items-center gap-2">
            <Activity className="text-primary" /> All Message History
          </h1>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-dark border border-gray-800 d-flex align-items-center gap-2 px-3"
            onClick={exportToCSV}
            disabled={serverData.totalRecords === 0}
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Data Table Section */}
      <div className="enterprise-card overflow-hidden">
        <div className="border-bottom border-gray-800 bg-dark-surface p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div className="d-flex gap-2">
            {["all", "not_opened", "opened", "clicked"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`btn btn-sm px-3 rounded-pill transition-all ${activeTab === tab ? "bg-primary text-white border-primary" : "btn-dark text-gray-400 border-gray-800"}`}
              >
                {tab === "all"
                  ? "All Activity"
                  : tab === "not_opened"
                    ? "Not Opened"
                    : tab === "opened"
                      ? "Opened Email"
                      : "Clicked Links"}
              </button>
            ))}
          </div>
          <div className="position-relative" style={{ width: "250px" }}>
            <Search
              size={16}
              className="position-absolute text-gray-500"
              style={{ top: "10px", left: "12px" }}
            />
            <input
              type="text"
              className="form-control form-control-sm bg-dark text-white border-gray-800 shadow-none ps-5 rounded-pill"
              placeholder="Search recipient or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="table-responsive" style={{ minHeight: "400px" }}>
          <table className="table table-dark table-hover mb-0 align-middle">
            <thead className="bg-dark-surface">
              <tr>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">
                  RECIPIENT
                </th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">
                  CAMPAIGN / SUBJECT
                </th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">
                  STATUS
                </th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">
                  LAST ACTIVITY
                </th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800 text-end">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedActivity.map((log, idx) => {
                const currentName = getCurrentName(log);
                
                return (
                <tr
                  key={idx}
                  className="border-gray-800 cursor-pointer"
                  onClick={() => setSelectedLogDetail(log)}
                >
                  <td className="px-4 py-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle bg-gray-800 d-flex align-items-center justify-content-center fw-bold text-gray-300"
                        style={{ width: 36, height: 36 }}
                      >
                        {currentName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-medium text-white">
                          {currentName}
                        </div>
                        <div className="text-gray-500 small">
                          {log.recipientEmail}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className="text-white text-truncate"
                      style={{ maxWidth: "250px" }}
                    >
                      {log.subject || log.templateName}
                    </div>
                    <div className="text-gray-500 small d-flex align-items-center gap-1">
                      <Calendar size={12} />{" "}
                      {log.date
                        ? formatDistanceToNow(new Date(log.date), {
                            addSuffix: true,
                          })
                        : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {log.tracking.isClicked ? (
                      <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1">
                        <MousePointerClick size={12} /> Clicked
                      </span>
                    ) : log.tracking.isOpened ? (
                      <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1">
                        <MailOpen size={12} /> Opened
                      </span>
                    ) : log.tracking.isDelivered ? (
                      <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1">
                        <Mail size={12} /> Not Opened
                      </span>
                    ) : log.tracking.isBounced ? (
                      <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1">
                        <XCircle size={12} /> Bounced
                      </span>
                    ) : (
                      <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1">
                        <Send size={12} /> Sent
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-300 small">
                      {log.tracking.timeline.length > 0
                        ? log.tracking.timeline[
                            log.tracking.timeline.length - 1
                          ].desc
                        : "No activity"}
                    </div>
                  </td>
                  <td
                    className="px-4 py-3 text-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="btn btn-sm btn-link text-gray-400 hover-text-white p-1">
                      <MoreVertical size={16} />
                    </button>
                  </td>
                </tr>
                );
              })}
              {paginatedActivity.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-gray-500">
                    <Search size={32} className="mb-3 opacity-50 mx-auto" />
                    No logs found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="border-top border-gray-800 p-3 d-flex justify-content-between align-items-center bg-dark-surface">
            <span className="text-gray-400 small">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, serverData.totalRecords)} of{" "}
              {serverData.totalRecords} entries
            </span>
            <div className="d-flex gap-1">
              <button
                className="btn btn-sm btn-dark border-gray-800 text-gray-300"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              {getPageNumbers().map((num) => (
                <button
                  key={num}
                  className={`btn btn-sm ${currentPage === num ? "btn-primary" : "btn-dark border-gray-800 text-gray-300"}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}
              <button
                className="btn btn-sm btn-dark border-gray-800 text-gray-300"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recipient Detail Modal */}
      <AnimatePresence>
        {selectedLogDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop-custom d-flex align-items-center justify-content-center"
            onClick={() => setSelectedLogDetail(null)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className="modal-content-custom enterprise-card w-100 mx-3 mx-md-auto d-flex flex-column overflow-hidden"
              style={{ maxWidth: "1200px", maxHeight: "90vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-bottom border-gray-800 p-4 d-flex justify-content-between align-items-center bg-dark-surface">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle bg-primary bg-opacity-20 d-flex align-items-center justify-content-center"
                    style={{ width: 48, height: 48 }}
                  >
                    <Users size={24} className="text-primary" />
                  </div>
                  <div>
                    <h5 className="mb-1 text-white fw-bold">
                      {getCurrentName(selectedLogDetail)}
                    </h5>
                    <div className="text-gray-400 small d-flex align-items-center gap-2">
                      <Mail size={14} /> {selectedLogDetail.recipientEmail}
                    </div>
                  </div>
                </div>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedLogDetail(null)}
                ></button>
              </div>

              <div className="row g-0 flex-grow-1 overflow-hidden">
                {/* Timeline Column */}
                <div
                  className="col-md-5 border-end border-gray-800 bg-dark-main p-4 overflow-y-auto custom-scrollbar"
                  style={{ maxHeight: "calc(90vh - 89px)" }}
                >
                  <h6 className="text-white fw-bold mb-4 d-flex align-items-center gap-2">
                    <Activity size={18} className="text-info" /> Activity
                    Timeline
                  </h6>
                  <div className="timeline-container ps-2 border-start border-gray-800 ms-2">
                    {selectedLogDetail.tracking.timeline.map((event, idx) => {
                      const Icon = event.icon;
                      return (
                        <div key={idx} className="position-relative mb-4 ps-4">
                          <div
                            className={`position-absolute bg-dark-main border border-gray-800 rounded-circle d-flex align-items-center justify-content-center`}
                            style={{
                              width: 28,
                              height: 28,
                              left: -14,
                              top: -4,
                            }}
                          >
                            <Icon size={14} className={event.color} />
                          </div>
                          <div>
                            <div className="text-white fw-medium mb-1 text-capitalize">
                              {event.type}
                            </div>
                            <div className="text-gray-400 small mb-1">
                              {event.desc}
                            </div>
                            <div
                              className="text-gray-500"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {format(event.time, "MMM dd, yyyy - hh:mm a")}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Details Column */}
                <div
                  className="col-md-7 p-4 bg-dark-surface overflow-y-auto custom-scrollbar"
                  style={{ maxHeight: "calc(90vh - 89px)" }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="text-white fw-bold mb-0">
                      Engagement Profile
                    </h6>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-2">
                        <PlayCircle size={14} /> Automation
                      </button>
                    </div>
                  </div>

                  <h6 className="text-gray-400 fw-semibold small text-uppercase tracking-wider mb-3">
                    Message Content
                  </h6>
                  {/* Email Body */}
                  <div className="bg-dark p-4 rounded-3 border border-gray-800 mb-3">
                    <div className="fw-bold text-white mb-2 pb-2 border-bottom border-gray-800">
                      Subject: {selectedLogDetail.subject || "(No Subject)"}
                    </div>
                    {selectedLogDetail.body ? (
                      <div
                        className="bg-white rounded p-3 text-dark mt-3 overflow-y-auto overflow-x-hidden custom-scrollbar email-preview-container"
                        style={{ maxHeight: "400px" }}
                        dangerouslySetInnerHTML={{
                          __html: `<style>
                            .email-preview-container { word-wrap: break-word !important; overflow-wrap: break-word !important; word-break: break-word !important; }
                            .email-preview-container * { max-width: 100% !important; box-sizing: border-box !important; }
                            .email-preview-container img { height: auto !important; }
                          </style>` + selectedLogDetail.body,
                        }}
                      />
                    ) : (
                      <div className="text-gray-500 text-center py-4">
                        Email body content not available
                      </div>
                    )}
                  </div>

                  {/* Links Clicked Panel */}
                  <h6 className="text-gray-400 fw-semibold small text-uppercase tracking-wider mb-3 mt-4 d-flex align-items-center gap-2">
                    <Link2 size={14} className="text-primary" />
                    Links Clicked
                    {linkClickData && (
                      <span className="badge bg-primary bg-opacity-20 text-primary ms-1 rounded-pill">
                        {linkClickData.totalClicks} click{linkClickData.totalClicks !== 1 ? "s" : ""}
                      </span>
                    )}
                  </h6>
                  <div className="rounded-3 border border-gray-800 overflow-hidden mb-3">
                    {linkClickLoading ? (
                      <div className="text-center py-4 text-gray-500">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                        Loading link data...
                      </div>
                    ) : !linkClickData || linkClickData.totalLinks === 0 ? (
                      <div className="text-center py-4 text-gray-500 small">
                        <MousePointerClick size={20} className="mb-2 opacity-40 mx-auto d-block" />
                        No trackable links found in this email.
                      </div>
                    ) : (
                      <div>
                        {linkClickData.linkStats.map((link, idx) => {
                          const maxClicks = Math.max(...linkClickData.linkStats.map(l => l.clickCount), 1);
                          const pct = Math.round((link.clickCount / maxClicks) * 100);
                          return (
                            <div
                              key={link.rid || idx}
                              className="px-4 py-3 border-bottom border-gray-800 last-child-no-border"
                              style={{ borderBottom: idx < linkClickData.linkStats.length - 1 ? undefined : "none" }}
                            >
                              <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
                                <div className="flex-grow-1 overflow-hidden">
                                  <div className="text-white small fw-medium text-truncate" title={link.label || link.url}>
                                    {link.label || link.url}
                                  </div>
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-500 d-flex align-items-center gap-1 text-decoration-none hover-text-primary"
                                    style={{ fontSize: "0.7rem" }}
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <ExternalLink size={10} />
                                    <span className="text-truncate" style={{ maxWidth: "260px" }}>{link.url}</span>
                                  </a>
                                </div>
                                <div className="text-end flex-shrink-0">
                                  <span
                                    className={`fw-bold ${
                                      link.clickCount > 0 ? "text-primary" : "text-gray-600"
                                    }`}
                                    style={{ fontSize: "1rem" }}
                                  >
                                    {link.clickCount}
                                  </span>
                                  <div className="text-gray-500" style={{ fontSize: "0.65rem" }}>clicks</div>
                                </div>
                              </div>
                              {/* Progress bar */}
                              <div
                                className="rounded-pill bg-dark mt-1"
                                style={{ height: 4, overflow: "hidden" }}
                              >
                                <div
                                  className="rounded-pill"
                                  style={{
                                    height: "100%",
                                    width: `${pct}%`,
                                    background:
                                      link.clickCount > 0
                                        ? "linear-gradient(90deg, #3b82f6, #6366f1)"
                                        : "#1e293b",
                                    transition: "width 0.5s ease",
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx="true">{`
        .bg-dark-main {
          background-color: #020617;
        }
        .bg-dark-surface {
          background-color: #0f172a;
        }
        .bg-dark {
          background-color: #1e293b !important;
        }
        .text-gray-300 {
          color: #cbd5e1 !important;
        }
        .text-gray-400 {
          color: #94a3b8 !important;
        }
        .text-gray-500 {
          color: #64748b !important;
        }
        .border-gray-800 {
          border-color: #1e293b !important;
        }
        .tracking-wider {
          letter-spacing: 0.05em;
        }

        .enterprise-card {
          background-color: #0f172a;
          border: 1px solid #1e293b;
          border-radius: 1rem;
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .cursor-pointer {
          cursor: pointer;
        }
        .hover-text-white:hover {
          color: #fff !important;
        }
        .transition-all {
          transition: all 0.2s ease-in-out;
        }

        .modal-backdrop-custom {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(2, 6, 23, 0.8);
          backdrop-filter: blur(4px);
          z-index: 1050;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #334155;
          border-radius: 10px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }

        .table-hover tbody tr:hover {
          background-color: rgba(255, 255, 255, 0.02) !important;
        }
      `}</style>
    </div>
  );
};

export default AllMessageHistory;
